import { c as _c } from "react/compiler-runtime";
import type { Entity, InvestigationAgentContext, OrgArtifact } from '@onefootprint/request-types/dashboard';
import { LoadingSpinner } from '@onefootprint/ui';
import dynamic from 'next/dynamic';
import type { ReactNode, Ref } from 'react';
import type { CitationPair } from '../../hooks/use-sources';
import { decodeBase64Utf8 } from '../../utils/decode-base64';
import reportCardsOf from '../../utils/report-cards-of';
import ArtifactCard from './artifact-card';
import { ArtifactEntityProvider } from './artifact-entity-context';
import { DecisionResultView, ImageView, JsonView, JudgementView, MarkdownView, PlainTextView, PreviewNotAvailable, ReportCardsView, RuleResultView } from './viewers';

// Shared padding shell for the non-markdown viewers so they sit in the same
// padded reading column as the markdown view. The viewers each manage their own
// `h-full overflow-auto` (they're also rendered directly by Investigations,
// which doesn't go through Preview), so this wrapper only contributes padding
// and the max-width constraint — never its own scroll container. On mobile the taller bottom padding
// ends the viewer's scroll area above the floating Ask Percy pill.
const ViewerLayout = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="h-full pt-6 pb-20 md:pb-6 px-4 md:px-16 max-w-(--main-content-max-width) mx-auto w-full">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

// PDF + CSV viewers pull in heavy deps (pdfjs worker, papaparse) and rely on browser-only globals.
const PdfView = dynamic(() => import('./viewers/pdf-view'), {
  ssr: false
});
const CsvView = dynamic(() => import('./viewers/csv-view'), {
  ssr: false
});
type ArtifactPreviewProps = {
  artifact: OrgArtifact;
  entity?: Entity;
  /** Base64-encoded artifact bytes. Decoded internally for text-based viewers. */
  contentBase64?: string;
  isLoading?: boolean;
  /** The investigation's agent — hydrates a rule-result artifact's rule definition from the graph. */
  agent?: InvestigationAgentContext;

  // Markdown-only props (effective only when artifact.mimeType === 'text/markdown'):
  /** Captures the rendered markdown DOM, the editor or an agent report's cards, for "Download as PDF". */
  contentRef?: Ref<HTMLDivElement>;
  editable?: boolean;
  onChange?: (next: string) => void;
  autoFocus?: boolean;
  onAutoFocused?: () => void;
  /** Override the decoded markdown content with the live edit string — set by the edit flow so the editor mirrors in-flight changes instead of re-decoding the persisted bytes. */
  editingMarkdownContent?: string;
  /** Remounts the editor or the report cards, e.g. on Discard to revert to the baseline content. */
  markdownEditorKey?: string;
  onCitationPairsChange?: (pairs: CitationPair[]) => void;
  onOpenSources?: () => void;
  onOpenDocument?: (documentIdentifier: string) => void;
};

// Dispatches by mime type. Text viewers receive decoded UTF-8; binary viewers
// receive a data URL built from the raw base64.
const ArtifactPreviewContent = t0 => {
  const $ = _c(81);
  const {
    artifact,
    contentBase64,
    isLoading: t1,
    agent,
    contentRef,
    editable,
    onChange,
    autoFocus,
    onAutoFocused,
    editingMarkdownContent,
    markdownEditorKey,
    onCitationPairsChange,
    onOpenSources,
    onOpenDocument
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  if (isLoading) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex h-full items-center justify-center"><LoadingSpinner /></div>;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  const {
    mimeType,
    name
  } = artifact;
  if (!contentBase64) {
    let t2;
    if ($[1] !== mimeType) {
      t2 = <PreviewNotAvailable mimeType={mimeType} />;
      $[1] = mimeType;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (artifact.metadata.kind === "agent_graph_judgement") {
    let t2;
    if ($[3] !== contentBase64) {
      t2 = decodeBase64Utf8(contentBase64);
      $[3] = contentBase64;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    const text = t2;
    if (text === null) {
      let t3;
      if ($[5] !== mimeType) {
        t3 = <PreviewNotAvailable mimeType={mimeType} />;
        $[5] = mimeType;
        $[6] = t3;
      } else {
        t3 = $[6];
      }
      return t3;
    }
    let t3;
    if ($[7] !== text) {
      t3 = <ViewerLayout><ArtifactCard title="Agent judgment"><JudgementView content={text} /></ArtifactCard></ViewerLayout>;
      $[7] = text;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (artifact.metadata.kind === "agent_graph_rule_result") {
    let t2;
    if ($[9] !== contentBase64) {
      t2 = decodeBase64Utf8(contentBase64);
      $[9] = contentBase64;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    const text_0 = t2;
    if (text_0 === null) {
      let t3;
      if ($[11] !== mimeType) {
        t3 = <PreviewNotAvailable mimeType={mimeType} />;
        $[11] = mimeType;
        $[12] = t3;
      } else {
        t3 = $[12];
      }
      return t3;
    }
    let t3;
    if ($[13] !== agent || $[14] !== artifact.metadata.data.nodeId || $[15] !== text_0) {
      t3 = <ViewerLayout><ArtifactCard title="Rule evaluation"><RuleResultView content={text_0} agent={agent} nodeId={artifact.metadata.data.nodeId} /></ArtifactCard></ViewerLayout>;
      $[13] = agent;
      $[14] = artifact.metadata.data.nodeId;
      $[15] = text_0;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    return t3;
  }
  if (artifact.metadata.kind === "agent_graph_decision_result") {
    let t2;
    if ($[17] !== contentBase64) {
      t2 = decodeBase64Utf8(contentBase64);
      $[17] = contentBase64;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    const text_1 = t2;
    if (text_1 === null) {
      let t3;
      if ($[19] !== mimeType) {
        t3 = <PreviewNotAvailable mimeType={mimeType} />;
        $[19] = mimeType;
        $[20] = t3;
      } else {
        t3 = $[20];
      }
      return t3;
    }
    let t3;
    if ($[21] !== agent || $[22] !== artifact.metadata.data.fieldKey || $[23] !== artifact.metadata.data.nodeId || $[24] !== text_1) {
      t3 = <ViewerLayout><ArtifactCard title="Decision"><DecisionResultView content={text_1} agent={agent} nodeId={artifact.metadata.data.nodeId} fieldKey={artifact.metadata.data.fieldKey} /></ArtifactCard></ViewerLayout>;
      $[21] = agent;
      $[22] = artifact.metadata.data.fieldKey;
      $[23] = artifact.metadata.data.nodeId;
      $[24] = text_1;
      $[25] = t3;
    } else {
      t3 = $[25];
    }
    return t3;
  }
  if (mimeType === "text/markdown") {
    let t2;
    if ($[26] !== contentBase64 || $[27] !== editingMarkdownContent) {
      t2 = editingMarkdownContent ?? decodeBase64Utf8(contentBase64);
      $[26] = contentBase64;
      $[27] = editingMarkdownContent;
      $[28] = t2;
    } else {
      t2 = $[28];
    }
    const text_2 = t2;
    if (text_2 === null) {
      let t3;
      if ($[29] !== mimeType) {
        t3 = <PreviewNotAvailable mimeType={mimeType} />;
        $[29] = mimeType;
        $[30] = t3;
      } else {
        t3 = $[30];
      }
      return t3;
    }
    let t3;
    if ($[31] !== artifact || $[32] !== editable || $[33] !== text_2) {
      t3 = editable ? null : reportCardsOf(artifact, text_2);
      $[31] = artifact;
      $[32] = editable;
      $[33] = text_2;
      $[34] = t3;
    } else {
      t3 = $[34];
    }
    const report = t3;
    if (report) {
      let t4;
      if ($[35] !== artifact.investigationId || $[36] !== contentRef || $[37] !== markdownEditorKey || $[38] !== onCitationPairsChange || $[39] !== onOpenDocument || $[40] !== onOpenSources || $[41] !== report) {
        t4 = <ReportCardsView key={markdownEditorKey} report={report} investigationId={artifact.investigationId} contentRef={contentRef} onCitationPairsChange={onCitationPairsChange} onOpenSources={onOpenSources} onOpenDocument={onOpenDocument} />;
        $[35] = artifact.investigationId;
        $[36] = contentRef;
        $[37] = markdownEditorKey;
        $[38] = onCitationPairsChange;
        $[39] = onOpenDocument;
        $[40] = onOpenSources;
        $[41] = report;
        $[42] = t4;
      } else {
        t4 = $[42];
      }
      return t4;
    }
    let t4;
    if ($[43] !== artifact.investigationId || $[44] !== autoFocus || $[45] !== contentRef || $[46] !== editable || $[47] !== markdownEditorKey || $[48] !== onAutoFocused || $[49] !== onChange || $[50] !== onCitationPairsChange || $[51] !== onOpenDocument || $[52] !== onOpenSources || $[53] !== text_2) {
      t4 = <MarkdownView key={markdownEditorKey} content={text_2} investigationId={artifact.investigationId} contentRef={contentRef} editable={editable} onChange={onChange} autoFocus={autoFocus} onAutoFocused={onAutoFocused} onCitationPairsChange={onCitationPairsChange} onOpenSources={onOpenSources} onOpenDocument={onOpenDocument} />;
      $[43] = artifact.investigationId;
      $[44] = autoFocus;
      $[45] = contentRef;
      $[46] = editable;
      $[47] = markdownEditorKey;
      $[48] = onAutoFocused;
      $[49] = onChange;
      $[50] = onCitationPairsChange;
      $[51] = onOpenDocument;
      $[52] = onOpenSources;
      $[53] = text_2;
      $[54] = t4;
    } else {
      t4 = $[54];
    }
    return t4;
  }
  if (mimeType === "text/plain") {
    let t2;
    if ($[55] !== contentBase64) {
      t2 = decodeBase64Utf8(contentBase64);
      $[55] = contentBase64;
      $[56] = t2;
    } else {
      t2 = $[56];
    }
    const text_3 = t2;
    if (text_3 === null) {
      let t3;
      if ($[57] !== mimeType) {
        t3 = <PreviewNotAvailable mimeType={mimeType} />;
        $[57] = mimeType;
        $[58] = t3;
      } else {
        t3 = $[58];
      }
      return t3;
    }
    let t3;
    if ($[59] !== text_3) {
      t3 = <ViewerLayout><PlainTextView content={text_3} /></ViewerLayout>;
      $[59] = text_3;
      $[60] = t3;
    } else {
      t3 = $[60];
    }
    return t3;
  }
  if (mimeType === "application/json") {
    let t2;
    if ($[61] !== contentBase64) {
      t2 = decodeBase64Utf8(contentBase64);
      $[61] = contentBase64;
      $[62] = t2;
    } else {
      t2 = $[62];
    }
    const text_4 = t2;
    if (text_4 === null) {
      let t3;
      if ($[63] !== mimeType) {
        t3 = <PreviewNotAvailable mimeType={mimeType} />;
        $[63] = mimeType;
        $[64] = t3;
      } else {
        t3 = $[64];
      }
      return t3;
    }
    let t3;
    if ($[65] !== text_4) {
      t3 = <ViewerLayout><JsonView content={text_4} /></ViewerLayout>;
      $[65] = text_4;
      $[66] = t3;
    } else {
      t3 = $[66];
    }
    return t3;
  }
  if (mimeType === "text/csv") {
    let t2;
    if ($[67] !== contentBase64) {
      t2 = decodeBase64Utf8(contentBase64);
      $[67] = contentBase64;
      $[68] = t2;
    } else {
      t2 = $[68];
    }
    const text_5 = t2;
    if (text_5 === null) {
      let t3;
      if ($[69] !== mimeType) {
        t3 = <PreviewNotAvailable mimeType={mimeType} />;
        $[69] = mimeType;
        $[70] = t3;
      } else {
        t3 = $[70];
      }
      return t3;
    }
    let t3;
    if ($[71] !== text_5) {
      t3 = <ViewerLayout><CsvView content={text_5} /></ViewerLayout>;
      $[71] = text_5;
      $[72] = t3;
    } else {
      t3 = $[72];
    }
    return t3;
  }
  if (mimeType === "application/pdf") {
    const t2 = `data:application/pdf;base64,${contentBase64}`;
    let t3;
    if ($[73] !== name || $[74] !== t2) {
      t3 = <ViewerLayout><PdfView src={t2} fileName={name} /></ViewerLayout>;
      $[73] = name;
      $[74] = t2;
      $[75] = t3;
    } else {
      t3 = $[75];
    }
    return t3;
  }
  if (mimeType.startsWith("image/")) {
    const t2 = `data:${mimeType};base64,${contentBase64}`;
    let t3;
    if ($[76] !== name || $[77] !== t2) {
      t3 = <ViewerLayout><ImageView src={t2} fileName={name} /></ViewerLayout>;
      $[76] = name;
      $[77] = t2;
      $[78] = t3;
    } else {
      t3 = $[78];
    }
    return t3;
  }
  let t2;
  if ($[79] !== mimeType) {
    t2 = <PreviewNotAvailable mimeType={mimeType} />;
    $[79] = mimeType;
    $[80] = t2;
  } else {
    t2 = $[80];
  }
  return t2;
};
const ArtifactPreview = t0 => {
  const $ = _c(8);
  let entity;
  let props;
  if ($[0] !== t0) {
    ({
      entity,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = entity;
    $[2] = props;
  } else {
    entity = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== props) {
    t1 = <ArtifactPreviewContent {...props} />;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== entity || $[6] !== t1) {
    t2 = <ArtifactEntityProvider entity={entity}>{t1}</ArtifactEntityProvider>;
    $[5] = entity;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export default ArtifactPreview;